﻿import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  TextField,
  DialogActions,
} from "@mui/material";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import { Close as CloseIcon } from "@mui/icons-material";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useDispatch, useSelector } from "react-redux";

const AddEditCustomField = ({
  open,
  onClose,
  fetchCustomFields,
  isEditing,
  initialData,
}) => {
  const dispatch = useDispatch();

  // Start with a clean, empty object for a fresh form
  const [formData, setFormData] = useState({});

  // State to hold the inline error message
  const [fieldCodeError, setFieldCodeError] = useState("");

  // Listen for when the modal opens and populate data if editing
  useEffect(() => {
    if (open) {
      if (isEditing && initialData) {
        // Map the grid row data to the form state
        setFormData({
          fieldId: initialData.ConnectionCustomFieldId,
          fieldCode: initialData.CustomFieldCode,
          fieldName: initialData.CustomFieldName,
          fieldDescription: initialData.CustomFieldDescription,
        });
      } else {
        // When adding, ensure the form is totally empty
        setFormData({});
      }
    }
  }, [open, isEditing, initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear the error as soon as user start typing to fix fieldCode validation errors
    if (name === "fieldCode") {
      setFieldCodeError("");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    // Reset field code error just in case
    setFieldCodeError("");

    if (Object.keys(formData).length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          message: "Please fill out the required fields to continue",
        }),
      );
      return;
    }

    // Use inline error for missing code
    if (!formData.fieldCode) {
      setFieldCodeError("Please enter the custom field code.");
      return;
    }

    // Use inline error for spaces or dots
    if (formData.fieldCode.includes(" ") || formData.fieldCode.includes(".")) {
      setFieldCodeError("Field Code cannot contain spaces or dots.");
      return;
    }

    const payload = {
      CustomFieldCode: formData.fieldCode,
      CustomFieldName: formData.fieldName || "",
      CustomFieldDescription: formData.fieldDescription || "",
    };

    if (isEditing) {
      payload.ConnectionCustomFieldId = formData.fieldId;
    }

    axios
      .post(`/connectioncustomfield/custom-field`, payload)
      .then((response) => {
        if (response.data.alreadyExists || response.data.inUse) {
          dispatch(
            createAlert({
              status: "Warning",
              alertType: "single",
              message: response.data.message,
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: response.data.message,
            }),
          );
        }
        fetchCustomFields();
        setFormData({});
        onClose();
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
  };

  const handleCancel = () => {
    onClose();
    setFormData({});
    setFieldCodeError(""); // Clear error on cancel
  };

  return (
    <Container>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={"md"}
        fullWidth={true}
        PaperProps={{ style: { borderRadius: 15 } }}
      >
        <DialogTitle
          className="add-crossref-dialog-title"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            fontSize: "24px",
            borderRadius: "15px 15px 0 0",
            padding: "16px 24px",
          }}
        >
          <div style={{ fontWeight: "var(--font-semi-bold)" }}>
            {isEditing ? "Edit Custom Field" : "Add Custom Field"}
          </div>
          <IconButton
            className="closeIconButton"
            style={{ color: "var(--default-light)" }}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          className="dialogContent"
          style={{
            backgroundColor: "var(--disabled)",
            padding: "35px",
            minWidth: 400,
          }}
        >
          <Container>
            <Container maxWidth="md">
              <Grid
                container
                spacing={3}
                alignItems="center"
                sx={{ mt: 2, mb: 2 }}
              >
                <Grid item xs={3}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Field Code <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    name="fieldCode"
                    value={formData.fieldCode || ""}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    error={!!fieldCodeError}
                    helperText={fieldCodeError}
                    sx={{ width: "60%" }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Field Name
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    name="fieldName"
                    value={formData.fieldName || ""}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "60%" }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Field Description
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    name="fieldDescription"
                    value={formData.fieldDescription || ""}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "60%" }}
                  />
                </Grid>
              </Grid>
            </Container>
          </Container>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <ShepButton variant="secondary" onClick={handleCancel}>
            Cancel
          </ShepButton>
          <ShepButton onClick={handleAdd}>
            {isEditing ? "Update" : "Add"}
          </ShepButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddEditCustomField;
